import { useState } from 'react';
import { Badge, Button, Group, Select, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowDown, IconArrowUp, IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { EDO_STATUS_COLORS, EDO_STATUS_LABELS, updsApi } from '../api/upd';
import { incomingUpdsApi, INCOMING_STATUS_COLORS, INCOMING_STATUS_LABELS } from '../api/incoming-upd';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

type Direction = 'OUT' | 'IN';

interface DocflowRow {
  key: string;
  direction: Direction;
  id: string;
  number: string;
  date: string;
  counterpartyName: string;
  organizationName: string;
  total: string;
  statusLabel: string;
  statusColor: string;
}

const DIRECTION_OPTIONS = [
  { value: 'OUT', label: 'Исходящие' },
  { value: 'IN', label: 'Входящие' },
];

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

/**
 * Единый список документооборота — как в 1С, где входящие и исходящие УПД
 * показываются вместе с колонкой направления, а не двумя разрозненными реестрами.
 * Каждая строка ведёт на свою карточку (исходящий/входящий УПД — разные модели
 * с разным жизненным циклом, объединять их в одну сущность в БД не нужно).
 */
export function DocflowPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canRegisterIncoming = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [direction, setDirection] = useState<Direction | null>(null);

  const outgoing = useQuery({
    queryKey: ['upds', { search: debouncedSearch, forDocflow: true }],
    queryFn: () => updsApi.list({ search: debouncedSearch, pageSize: 200 }),
    enabled: direction !== 'IN',
  });

  const incoming = useQuery({
    queryKey: ['incoming-upds', { search: debouncedSearch, forDocflow: true }],
    queryFn: () => incomingUpdsApi.list({ search: debouncedSearch, pageSize: 200 }),
    enabled: direction !== 'OUT',
  });

  const outgoingRows: DocflowRow[] = (direction !== 'IN' ? outgoing.data?.items ?? [] : []).map((item) => ({
    key: `out-${item.id}`,
    direction: 'OUT',
    id: item.id,
    number: item.number,
    date: item.date,
    counterpartyName: item.counterparty.shortName,
    organizationName: item.organization.shortName,
    total: item.total,
    statusLabel: EDO_STATUS_LABELS[item.edoStatus],
    statusColor: EDO_STATUS_COLORS[item.edoStatus],
  }));

  const incomingRows: DocflowRow[] = (direction !== 'OUT' ? incoming.data?.items ?? [] : []).map((item) => ({
    key: `in-${item.id}`,
    direction: 'IN',
    id: item.id,
    number: item.number,
    date: item.date,
    counterpartyName: item.counterparty.shortName,
    organizationName: item.organization.shortName,
    total: item.total,
    statusLabel: INCOMING_STATUS_LABELS[item.status],
    statusColor: INCOMING_STATUS_COLORS[item.status],
  }));

  const rows = [...outgoingRows, ...incomingRows].sort((a, b) => b.date.localeCompare(a.date));
  const isLoading = outgoing.isLoading || incoming.isLoading;

  function openRow(row: DocflowRow) {
    navigate(row.direction === 'OUT' ? `/upd/${row.id}` : `/incoming-upd/${row.id}`);
  }

  return (
    <RegistryPage
      title="Документооборот"
      action={
        canRegisterIncoming && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/incoming-upd/new')}>
            Зарегистрировать входящий
          </Button>
        )
      }
      description={
        <Text size="sm" c="dimmed">
          Все УПД по ЭДО — отправленные нашим контрагентам и присланные поставщиками — в одном списке.
          Направление показывает стрелка перед номером. Исходящий УПД создаётся из счёта на оплату.
        </Text>
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по номеру или контрагенту"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Select
            placeholder="Все направления"
            data={DIRECTION_OPTIONS}
            value={direction}
            onChange={(v) => setDirection(v as Direction | null)}
            clearable
            w={180}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40} />
            <Table.Th>Номер</Table.Th>
            <Table.Th>Дата</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Контрагент</Table.Th>
            <Table.Th>Сумма</Table.Th>
            <Table.Th>Статус</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.key} style={{ cursor: 'pointer' }} onClick={() => openRow(row)}>
              <Table.Td>
                {row.direction === 'OUT' ? (
                  <IconArrowUp size={16} color="var(--mantine-color-blue-6)" title="Исходящий" />
                ) : (
                  <IconArrowDown size={16} color="var(--mantine-color-teal-6)" title="Входящий" />
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>
                  № {row.number}
                </Text>
              </Table.Td>
              <Table.Td>{formatDate(row.date)}</Table.Td>
              <Table.Td>{row.organizationName}</Table.Td>
              <Table.Td>{row.counterpartyName}</Table.Td>
              <Table.Td>{formatMoney(row.total)}</Table.Td>
              <Table.Td>
                <Badge color={row.statusColor} variant="light">
                  {row.statusLabel}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!isLoading && rows.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Документы не найдены
        </Text>
      )}
    </RegistryPage>
  );
}
